import { useEffect, useState } from "react";
import { useFlows, Transaction, MatchedFlow } from "../../../../../stores/flowsStore";
import { useFlowsSavedDocuments } from "../../../../../stores/flowsSavedDocumentsStore";
import { EmailData } from "./SubmitBundleModal/types";
import { TradeRow, FXSpotRow, WireTransferRow } from "./types";

const createEmptyTradeRow = (): TradeRow => ({
  id: crypto.randomUUID(),
  transaction_type: "buy",
  client_id: "",
  custodian_id: "",
  instrument_isin: "",
  side: "buy",
  amount: 0,
  currency: "USD",
});

const createEmptyFXRow = (): FXSpotRow => ({
  id: crypto.randomUUID(),
  client_id: "",
  custodian_id: "",
  buy_amount: 0,
  buy_currency: "USD",
  sell_currency: "EUR",
});

const createEmptyWireRow = (): WireTransferRow => ({
  id: crypto.randomUUID(),
  client_id: "",
  amount: 0,
  currency: "USD",
  from_custodian_id: "",
  to_custodian_id: "",
});

export const useTransactionsView = () => {
  const {
    setSubView,
    fetchTransactions,
    fetchDataSources,
    transactions,
    pendingTransactions,
    validateTransactions,
    submitTransactions,
    createBulkTransactions,
    sendTransactionEmail,
    matchFlowsForTransaction,
    recordFlowRun,
    deleteTransaction,
    clients,
    custodians,
    instruments,
    error,
    clearError,
  } = useFlows();

  const { savedGeneratedDocuments, fetchSavedGeneratedDocuments } = useFlowsSavedDocuments();

  // Accordion expansion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    trades: true,
    fxspot: false,
    wire: false,
  });

  // Inline row data
  const [tradeRows, setTradeRows] = useState<TradeRow[]>([createEmptyTradeRow()]);
  const [fxRows, setFxRows] = useState<FXSpotRow[]>([createEmptyFXRow()]);
  const [wireRows, setWireRows] = useState<WireTransferRow[]>([createEmptyWireRow()]);

  // Pending transactions filters
  const [clientFilter, setClientFilter] = useState("");
  const [custodianFilter, setCustodianFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Submit bundle modal state
  const [submitBundleModalOpen, setSubmitBundleModalOpen] = useState(false);
  const [selectedBundleTransactions, setSelectedBundleTransactions] = useState<Transaction[]>([]);
  const [selectedCustodianName, setSelectedCustodianName] = useState("");
  const [selectedCustodianEmail, setSelectedCustodianEmail] = useState<string | undefined>(undefined);
  const [matchedFlowForBundle, setMatchedFlowForBundle] = useState<MatchedFlow | null>(null);

  useEffect(() => {
    fetchTransactions();
    fetchDataSources();
    fetchSavedGeneratedDocuments();
  }, [fetchTransactions, fetchDataSources, fetchSavedGeneratedDocuments]);

  const handleAccordionChange = (section: string) => () => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Trade row handlers
  const handleTradeChange = (id: string, field: keyof TradeRow, value: string | number) => {
    setTradeRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddTradeRow = async () => {
    const lastRow = tradeRows[tradeRows.length - 1];
    if (lastRow.client_id && lastRow.amount > 0) {
      const client = clients.find((c) => c.client_id === lastRow.client_id);
      const instrument = instruments.find((i) => i.isin === lastRow.instrument_isin);

      await createBulkTransactions([{
        transaction_type: "trade",
        client_id: lastRow.client_id,
        client_name: client?.client_name || "",
        custodian_id: lastRow.custodian_id,
        custodian_name: custodians.find((c) => c.custodian_id === lastRow.custodian_id)?.custodian_name || "",
        instrument_isin: lastRow.instrument_isin,
        instrument_name: instrument?.name || "",
        side: lastRow.side,
        amount: lastRow.amount,
        currency: lastRow.currency,
      }]);

      setTradeRows([...tradeRows, createEmptyTradeRow()]);
    } else {
      setTradeRows([...tradeRows, createEmptyTradeRow()]);
    }
  };

  const handleRemoveTradeRow = (id: string) => {
    if (tradeRows.length > 1) {
      setTradeRows(tradeRows.filter((row) => row.id !== id));
    }
  };

  const handleReAddTradeRow = async (row: TradeRow) => {
    if (row.client_id && row.amount > 0) {
      const client = clients.find((c) => c.client_id === row.client_id);
      const instrument = instruments.find((i) => i.isin === row.instrument_isin);

      await createBulkTransactions([{
        transaction_type: "trade",
        client_id: row.client_id,
        client_name: client?.client_name || "",
        custodian_id: row.custodian_id,
        custodian_name: custodians.find((c) => c.custodian_id === row.custodian_id)?.custodian_name || "",
        instrument_isin: row.instrument_isin,
        instrument_name: instrument?.name || "",
        side: row.side,
        amount: row.amount,
        currency: row.currency,
      }]);
    }
  };

  // FX Spot row handlers
  const handleFXChange = (id: string, field: keyof FXSpotRow, value: string | number) => {
    setFxRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddFXRow = async () => {
    const lastRow = fxRows[fxRows.length - 1];
    if (lastRow.client_id && lastRow.buy_amount > 0) {
      const client = clients.find((c) => c.client_id === lastRow.client_id);

      await createBulkTransactions([{
        transaction_type: "fx_spot",
        client_id: lastRow.client_id,
        client_name: client?.client_name || "",
        custodian_id: lastRow.custodian_id,
        custodian_name: custodians.find((c) => c.custodian_id === lastRow.custodian_id)?.custodian_name || "",
        buy_currency: lastRow.buy_currency,
        sell_currency: lastRow.sell_currency,
        amount: lastRow.buy_amount,
        currency: lastRow.buy_currency,
      }]);

      setFxRows([...fxRows, createEmptyFXRow()]);
    } else {
      setFxRows([...fxRows, createEmptyFXRow()]);
    }
  };

  const handleRemoveFXRow = (id: string) => {
    if (fxRows.length > 1) {
      setFxRows(fxRows.filter((row) => row.id !== id));
    }
  };

  const handleReAddFXRow = async (row: FXSpotRow) => {
    if (row.client_id && row.buy_amount > 0) {
      const client = clients.find((c) => c.client_id === row.client_id);

      await createBulkTransactions([{
        transaction_type: "fx_spot",
        client_id: row.client_id,
        client_name: client?.client_name || "",
        custodian_id: row.custodian_id,
        custodian_name: custodians.find((c) => c.custodian_id === row.custodian_id)?.custodian_name || "",
        buy_currency: row.buy_currency,
        sell_currency: row.sell_currency,
        amount: row.buy_amount,
        currency: row.buy_currency,
      }]);
    }
  };

  // Wire Transfer row handlers
  const handleWireChange = (id: string, field: keyof WireTransferRow, value: string | number) => {
    setWireRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddWireRow = async () => {
    const lastRow = wireRows[wireRows.length - 1];
    if (lastRow.client_id && lastRow.amount > 0) {
      const client = clients.find((c) => c.client_id === lastRow.client_id);

      await createBulkTransactions([{
        transaction_type: "wire_transfer",
        client_id: lastRow.client_id,
        client_name: client?.client_name || "",
        from_custodian_id: lastRow.from_custodian_id,
        from_custodian_name: custodians.find((c) => c.custodian_id === lastRow.from_custodian_id)?.custodian_name || "",
        to_custodian_id: lastRow.to_custodian_id,
        to_custodian_name: custodians.find((c) => c.custodian_id === lastRow.to_custodian_id)?.custodian_name || "",
        amount: lastRow.amount,
        currency: lastRow.currency,
      }]);

      setWireRows([...wireRows, createEmptyWireRow()]);
    } else {
      setWireRows([...wireRows, createEmptyWireRow()]);
    }
  };

  const handleRemoveWireRow = (id: string) => {
    if (wireRows.length > 1) {
      setWireRows(wireRows.filter((row) => row.id !== id));
    }
  };

  const handleReAddWireRow = async (row: WireTransferRow) => {
    if (row.client_id && row.amount > 0) {
      const client = clients.find((c) => c.client_id === row.client_id);

      await createBulkTransactions([{
        transaction_type: "wire_transfer",
        client_id: row.client_id,
        client_name: client?.client_name || "",
        from_custodian_id: row.from_custodian_id,
        from_custodian_name: custodians.find((c) => c.custodian_id === row.from_custodian_id)?.custodian_name || "",
        to_custodian_id: row.to_custodian_id,
        to_custodian_name: custodians.find((c) => c.custodian_id === row.to_custodian_id)?.custodian_name || "",
        amount: row.amount,
        currency: row.currency,
      }]);
    }
  };

  const handleClearAll = () => {
    setTradeRows([createEmptyTradeRow()]);
    setFxRows([createEmptyFXRow()]);
    setWireRows([createEmptyWireRow()]);
  };

  const handleValidate = async () => {
    await validateTransactions();
  };

  const handleSubmitAll = async () => {
    await submitTransactions();
  };

  const handleOpenSubmitBundle = async (txn: Transaction) => {
    const custodianTransactions = transactions.filter(
      (t) => t.custodian_id === txn.custodian_id ||
             t.to_custodian_id === txn.to_custodian_id ||
             t.from_custodian_id === txn.from_custodian_id
    );

    const custodianName = txn.custodian_name || txn.to_custodian_name || txn.from_custodian_name || "Custodian";

    setSelectedBundleTransactions(custodianTransactions.length > 0 ? custodianTransactions : [txn]);
    setSelectedCustodianName(custodianName);
    setSelectedCustodianEmail(undefined);

    const transactionData = {
      client_name: txn.client_name,
      client_id: txn.client_id,
      custodian_name: custodianName,
      custodian_id: txn.custodian_id || txn.to_custodian_id || txn.from_custodian_id,
      from_custodian_name: txn.from_custodian_name,
      to_custodian_name: txn.to_custodian_name,
      transaction_type: txn.transaction_type,
      amount: txn.amount,
      price: txn.price || txn.amount,
      buy_amount: txn.amount,
      currency: txn.currency,
      buy_currency: txn.buy_currency,
      sell_currency: txn.sell_currency,
      instrument_name: txn.instrument_name,
      instrument_isin: txn.instrument_isin,
      side: txn.side,
      quantity: txn.quantity,
      transaction_date: txn.trade_date || new Date().toLocaleDateString(),
    };

    const matchedFlows = await matchFlowsForTransaction(transactionData);
    const flowWithAction = matchedFlows.find((f) => f.email_action || f.template_action);
    setMatchedFlowForBundle(flowWithAction || null);

    setSubmitBundleModalOpen(true);
  };

  const handleCloseSubmitBundle = () => {
    setSubmitBundleModalOpen(false);
    setMatchedFlowForBundle(null);
  };

  const handleSendEmail = async (emailData: EmailData) => {
    const transactionIds = selectedBundleTransactions.map((t) => t.identifier);
    const documentIds = emailData.attachments
      .filter((a) => a.type === "generated" && a.documentId)
      .map((a) => a.documentId!);
    const localFiles = emailData.attachments
      .filter((a) => a.type === "local" && a.file)
      .map((a) => a.file!);

    const success = await sendTransactionEmail({
      to: emailData.to,
      cc: emailData.cc || undefined,
      subject: emailData.subject,
      body: emailData.body,
      transactionIds,
      documentIds: documentIds.length > 0 ? documentIds : undefined,
      localFiles: localFiles.length > 0 ? localFiles : undefined,
    });

    if (matchedFlowForBundle) {
      const firstTransaction = selectedBundleTransactions[0];
      await recordFlowRun({
        flow_id: matchedFlowForBundle.flow_id,
        flow_name: matchedFlowForBundle.flow_name,
        client_id: firstTransaction?.client_id,
        client_name: firstTransaction?.client_name,
        trigger_data: {
          client_name: firstTransaction?.client_name,
          client_id: firstTransaction?.client_id,
          custodian_name: selectedCustodianName,
          transaction_type: firstTransaction?.transaction_type,
          amount: firstTransaction?.amount,
          currency: firstTransaction?.currency,
        },
        status: success ? "completed" : "failed",
      });
    }
  };

  const filteredTransactions = transactions.filter((txn) => {
    if (txn.status === "submitted") return false;
    if (clientFilter && txn.client_id !== clientFilter) return false;
    if (custodianFilter && txn.custodian_id !== custodianFilter) return false;
    if (typeFilter && txn.transaction_type !== typeFilter) return false;
    if (statusFilter && txn.status !== statusFilter) return false;
    return true;
  });

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "trade": return "Trade";
      case "fx_spot": return "FX Spot";
      case "wire_transfer": return "Wire";
      default: return type;
    }
  };

  return {
    // Navigation
    setSubView,
    // Data
    clients,
    custodians,
    instruments,
    pendingTransactions,
    filteredTransactions,
    savedGeneratedDocuments,
    error,
    clearError,
    // Accordion
    expandedSections,
    handleAccordionChange,
    // Trade rows
    tradeRows,
    handleTradeChange,
    handleAddTradeRow,
    handleRemoveTradeRow,
    handleReAddTradeRow,
    // FX rows
    fxRows,
    handleFXChange,
    handleAddFXRow,
    handleRemoveFXRow,
    handleReAddFXRow,
    // Wire rows
    wireRows,
    handleWireChange,
    handleAddWireRow,
    handleRemoveWireRow,
    handleReAddWireRow,
    // Filters
    clientFilter,
    setClientFilter,
    custodianFilter,
    setCustodianFilter,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    // Actions
    handleClearAll,
    handleValidate,
    handleSubmitAll,
    deleteTransaction,
    // Submit bundle
    submitBundleModalOpen,
    selectedBundleTransactions,
    selectedCustodianName,
    selectedCustodianEmail,
    matchedFlowForBundle,
    handleOpenSubmitBundle,
    handleCloseSubmitBundle,
    handleSendEmail,
    // Utils
    getTypeLabel,
  };
};
